import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useNavigate, useLocation } from 'react-router-dom';
import { OutletContext } from '../../layout/Layout';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { Card, CardBody, Grid, GridItem, Label } from '@patternfly/react-core';
import './Discover.scss';
import { DiscoverCategory, DiscoverContext, QuickFilter } from '../../types/discover';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { SubNavContext } from '../../helpers/sub-nav-contexts/sub-nav-context';
import { TQuestion } from '../../api/questions/Questions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import ChatInterface from '../../components/data-builder/ChatInterface';
import { SavedChat, SavedChats } from './SavedChats';
import { ChatActions } from '../../api/lambda/ChatActions';
import { ChatSession } from '../../api/types';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { useApplication } from '../../components/user/ApplicationProvider';

type Props = {
	pageContext: DiscoverContext;
};
const AdvancedAI = (props: Props) => {
	const navigate = useNavigate();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [categories, setCategories] = useState<DiscoverCategory[]>();
	const [hasLoadedCategories, setHasLoadedCategories] = useState<boolean>(false);
	const [quickFilters, setQuickFilters] = useState<QuickFilter[]>();
	const [filteredQuickFilters, setFilteredQuickFilters] = useState<QuickFilter[]>();
	const [_, setSelectedDate] = useState<TNewDateRange>();
	const [subNavContext, setSubNavContext] = useState<SubNavContext>();
	const [allQuestions, setAllQuestions] = useState<TQuestion[]>();
	const [aiSearchEnabled, setAiSearchEnabled] = useState(false);
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const termParam = params.get('term') ?? undefined;
	// initialTerm will be passed into the chat as the auto-submit message (if present)
	const initialTerm = termParam;
	const [selectedSession, setSelectedSession] = useState<string | undefined>(
		new Date().getTime().toString()
	);
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>();
	const { user } = useApplication();
	const [loading, setLoading] = useState(false);
	const [savedChats, setSavedChats] = useState<SavedChat[]>([]);
	const [currentChatDate, setCurrentChatDate] = useState<string>();

	const handleSelectChat = (sessionId: string, jobId: string) => {
		setSelectedSession(sessionId);
		setSelectedJobId(jobId);
		setCurrentChatDate(savedChats.find((x) => x.session_id == sessionId)?.last_activity ?? '');
	};

	const handleNewChat = () => {
		setSelectedSession(new Date().getTime().toString());
		setSelectedJobId(undefined);
		setCurrentChatDate(undefined);
		void fetchSavedChats();
	};

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle=""
					pageDescription=""
					subNavContext={undefined}
				/>
			),
			sidebarContext: null,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	// update the search handler (URL-escape terms)
	const handleSearch = (_event: any, value: string) => {
		const encoded = encodeURIComponent(value || '');
		if (aiSearchEnabled) {
			navigate(`/discover/analyst?term=${encoded}`);
		} else {
			navigate(`/discover/search?term=${encoded}`);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && searchTerm && searchTerm.trim().length > 0) {
			const encoded = encodeURIComponent(searchTerm);
			if (aiSearchEnabled) {
				navigate(`/discover/analyst?term=${encoded}`);
			} else {
				navigate(`/discover/search?term=${encoded}`);
			}
		}
	};

	const fetchSavedChats = async () => {
		setLoading(true);
		try {
			const userIdentifer = user.account + getEnvironmentCode();
			const response = await ChatActions.getInovationAIChatSessions(25, userIdentifer);

			if (response.sessions && response.sessions.length > 0) {
				// Convert ChatSession to SavedChat format
				const convertedChats: SavedChat[] = response.sessions.map(
					(session: ChatSession) => ({
						session_id: session.session_id,
						job_id: session.job_id ?? session.session_id,
						title: session.title ?? '',
						created_at: new Date(session.created_at!).toISOString().split('T')[0],
						numberOfMsgs: session.message_count ?? 1,
						last_activity: new Date(session.last_activity!).toISOString().split('T')[0],
					})
				);

				setSavedChats(convertedChats);
			}
		} catch (error) {
			console.error('Error fetching saved chats:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Grid span={12}>
			<GridItem span={2}>
				{/* Left sidebar with saved chats */}
				<SavedChats
					onSelectChat={handleSelectChat}
					onNewChat={handleNewChat}
					fetchSavedChats={fetchSavedChats}
					isLoading={loading}
					setLoading={setLoading}
					setSavedChats={setSavedChats}
					savedChats={savedChats}
				/>
			</GridItem>
			<GridItem span={10}>
				<Card className="discover-container advanced-ai-panel">
					<CardBody className="advanced-ai-body">
						<h1>
							<FontAwesomeIcon
								icon={faSparkles}
								style={{ paddingRight: '5px' }}
							/>
							<span className="insights-sticky-header__title"> AI Analyst</span>
						</h1>{' '}
						{currentChatDate && (
							<Label style={{ width: '190px' }}>
								Last Activity:{' '}
								<b style={{ paddingLeft: '3px' }}>{currentChatDate}</b>
							</Label>
						)}
						{/* Main chat area */}
						<ChatInterface
							isSinglePage={true}
							externalThread={false}
							autoSubmitMessage={initialTerm}
							useInnovationAI={true}
							canSaveChat={true}
							sessionId={selectedSession}
							contextId={selectedJobId}
							onAutoSubmitted={() => navigate('/discover/analyst', { replace: true })}
						/>
					</CardBody>
				</Card>
			</GridItem>
		</Grid>
	);
};

export default AdvancedAI;
